import type { ReactNode } from 'react';
import { create } from 'zustand';

import {
  DEFAULT_SIZE,
  MAXIMIZE_MARGIN,
} from '@/components/FloatingWindow/floatingWindow.constants';
import {
  cascadePosition,
  clampPosition,
  clampSize,
  getViewport,
  type Point,
  readPersistedSize,
  type Size,
  writePersistedSize,
} from '@/components/FloatingWindow/floatingWindowGeometry';

/**
 * Global registry of open FloatingWindow instances (window-manager state).
 *
 * Zustand (not TanStack Query) by the same deliberate exception as
 * `bulkJobs.store.ts`: this is transient client UI state tracking N concurrent
 * windows with their own geometry/focus lifecycle, not server data.
 *
 * The consumer passes the body as `children` (a rendered element) to
 * `openWindow`; the element is stored and rendered by the host — NOT by the
 * consumer's tree — so it survives the consumer unmounting (route/drawer
 * change) and the body keeps its own live data subscriptions (TanStack Query /
 * WebSocket). NOTE: a React element captures its props at open time, so pass
 * ids (`<ExploitFeed riskKey={k} />`) and let the body fetch, rather than
 * baking already-fetched data into the element. Do NOT pass a `() => ReactNode`
 * closure: it would freeze the opener's scope and go stale on unmount.
 *
 * `zIndex` is NOT stored: it is derived from `focusOrder` rank (see
 * `computeZIndex`), so focusing a window never reorders the rendered array
 * (which would reload in-body iframes/media). Only per-`type` size is persisted
 * to localStorage (concurrent instances of a type share a remembered size).
 */

export interface FloatingWindowInstance {
  /** `${type}:${key}` — stable identity for dedup and React keys. */
  id: string;
  /** Grouping key for remembered-size + cascade (e.g. 'constantine-exploit'). */
  type: string;
  title: string;
  /** Body element rendered by the host. */
  children: ReactNode;
  position: Point;
  size: Size;
  minimized: boolean;
  maximized: boolean;
  /** Geometry stashed before maximize, restored on un-maximize. */
  preMaxGeometry?: { position: Point; size: Size };
}

export interface OpenWindowOptions {
  /** Grouping key for remembered-size + cascade + dedup-id prefix. */
  type: string;
  /** Distinguishes concurrent instances of one type (e.g. a risk/run id). Defaults to `type` (singleton). */
  key?: string;
  title: string;
  /** Body element; the host renders it. Pass ids in props, not fetched data. */
  children: ReactNode;
  /** Explicit initial size; otherwise the persisted per-type size or DEFAULT_SIZE. */
  size?: Size;
}

interface FloatingWindowsState {
  instances: Record<string, FloatingWindowInstance>;
  /** Focus order; last element is the most-recently-focused (top of stack). */
  focusOrder: string[];

  openWindow: (opts: OpenWindowOptions) => string;
  closeWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  restoreWindow: (id: string) => void;
  toggleMaximize: (id: string) => void;
  setPosition: (id: string, position: Point) => void;
  setSize: (id: string, size: Size) => void;
  closeAllWindows: () => void;
  /** Test-only reset. */
  reset: () => void;
}

const makeId = (type: string, key?: string) => `${type}:${key ?? type}`;

/** Move `id` to the end (top) of focusOrder; no-op if already on top. */
function bringToFront(order: string[], id: string): string[] {
  if (order[order.length - 1] === id) return order;
  return [...order.filter(existing => existing !== id), id];
}

export const useFloatingWindowsStore = create<FloatingWindowsState>(
  (set, get) => ({
    instances: {},
    focusOrder: [],

    openWindow: ({ type, key, title, children, size }) => {
      const id = makeId(type, key);
      const existing = get().instances[id];

      if (existing) {
        // Re-open: refresh title/children, un-minimize, raise to top.
        set(state => ({
          instances: {
            ...state.instances,
            [id]: { ...existing, title, children, minimized: false },
          },
          focusOrder: bringToFront(state.focusOrder, id),
        }));
        return id;
      }

      const viewport = getViewport();
      const resolvedSize = clampSize(
        size ?? readPersistedSize(type) ?? DEFAULT_SIZE,
        viewport
      );
      const openCountOfType = Object.values(get().instances).filter(
        instance => instance.type === type
      ).length;
      const position = cascadePosition(openCountOfType, resolvedSize, viewport);

      set(state => ({
        instances: {
          ...state.instances,
          [id]: {
            id,
            type,
            title,
            children,
            position,
            size: resolvedSize,
            minimized: false,
            maximized: false,
          },
        },
        focusOrder: [...state.focusOrder, id],
      }));
      return id;
    },

    closeWindow: id => {
      set(state => {
        if (!state.instances[id]) return state;
        const instances = { ...state.instances };
        delete instances[id];
        return {
          instances,
          focusOrder: state.focusOrder.filter(existing => existing !== id),
        };
      });
    },

    focusWindow: id => {
      set(state => {
        if (!state.instances[id]) return state;
        return { focusOrder: bringToFront(state.focusOrder, id) };
      });
    },

    minimizeWindow: id => {
      set(state => {
        const instance = state.instances[id];
        if (!instance || instance.minimized) return state;
        return {
          instances: {
            ...state.instances,
            [id]: { ...instance, minimized: true },
          },
        };
      });
    },

    restoreWindow: id => {
      set(state => {
        const instance = state.instances[id];
        if (!instance) return state;
        return {
          instances: {
            ...state.instances,
            [id]: { ...instance, minimized: false },
          },
          focusOrder: bringToFront(state.focusOrder, id),
        };
      });
    },

    toggleMaximize: id => {
      set(state => {
        const instance = state.instances[id];
        if (!instance) return state;

        if (instance.maximized && instance.preMaxGeometry) {
          const { position, size } = instance.preMaxGeometry;
          return {
            instances: {
              ...state.instances,
              [id]: {
                ...instance,
                maximized: false,
                position,
                size,
                preMaxGeometry: undefined,
              },
            },
            focusOrder: bringToFront(state.focusOrder, id),
          };
        }

        // Maximize to (almost) the full viewport. Not persisted — the
        // remembered per-type size stays the user's chosen windowed size.
        const viewport = getViewport();
        const maxSize = clampSize(
          {
            w: viewport.w - 2 * MAXIMIZE_MARGIN,
            h: viewport.h - 2 * MAXIMIZE_MARGIN,
          },
          viewport
        );
        return {
          instances: {
            ...state.instances,
            [id]: {
              ...instance,
              maximized: true,
              preMaxGeometry: {
                position: instance.position,
                size: instance.size,
              },
              position: { x: MAXIMIZE_MARGIN, y: MAXIMIZE_MARGIN },
              size: maxSize,
            },
          },
          focusOrder: bringToFront(state.focusOrder, id),
        };
      });
    },

    setPosition: (id, position) => {
      set(state => {
        const instance = state.instances[id];
        if (!instance) return state;
        // Not persisted — concurrent same-type instances keep distinct
        // positions only for the session (cascade handles fresh opens).
        return {
          instances: {
            ...state.instances,
            [id]: {
              ...instance,
              position: clampPosition(position, instance.size),
            },
          },
        };
      });
    },

    setSize: (id, size) => {
      set(state => {
        const instance = state.instances[id];
        if (!instance) return state;
        const clamped = clampSize(size);
        // Persist per-type so the next window of this type opens at this size.
        writePersistedSize(instance.type, clamped);
        return {
          instances: {
            ...state.instances,
            [id]: { ...instance, size: clamped },
          },
        };
      });
    },

    closeAllWindows: () => set({ instances: {}, focusOrder: [] }),

    reset: () => set({ instances: {}, focusOrder: [] }),
  })
);
